import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../../../../app/rootReducer";
import {
  FieldMapping,
  CreateFieldMappingRequest,
} from "../../../../../types/partnerReports";
import {
  getPartnerFieldMappings,
  getActiveFieldMapping,
  getFieldMapping,
  createFieldMapping,
  updateFieldMapping,
  deleteFieldMapping,
  activateFieldMapping,
  deactivateFieldMapping,
} from "../../../../../api/partnerReports";

export interface FieldMappingsState {
  mappings: FieldMapping[];
  activeMapping: FieldMapping | null;
  selectedMapping: FieldMapping | null;
  loading: boolean;
  error: string | null;
  uploadProgress: {
    isUploading: boolean;
    progress: number;
  };
}

const initialState: FieldMappingsState = {
  mappings: [],
  activeMapping: null,
  selectedMapping: null,
  loading: false,
  error: null,
  uploadProgress: {
    isUploading: false,
    progress: 0,
  },
};

// Async Thunk Actions
export const fetchPartnerFieldMappingsAction = createAsyncThunk(
  "fieldMappings/fetchPartnerFieldMappings",
  async (partnerId: string, { rejectWithValue }) => {
    try {
      const mappings = await getPartnerFieldMappings(partnerId);
      return mappings;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch field mappings"
      );
    }
  }
);

export const fetchActiveFieldMappingAction = createAsyncThunk(
  "fieldMappings/fetchActiveFieldMapping",
  async (
    { partnerId, fileType }: { partnerId: string; fileType: string },
    { rejectWithValue }
  ) => {
    try {
      const mapping = await getActiveFieldMapping(partnerId, fileType);
      return mapping;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch active field mapping"
      );
    }
  }
);

export const fetchFieldMappingAction = createAsyncThunk(
  "fieldMappings/fetchFieldMapping",
  async (id: string, { rejectWithValue }) => {
    try {
      const mapping = await getFieldMapping(id);
      return mapping;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch field mapping"
      );
    }
  }
);

export const createFieldMappingAction = createAsyncThunk(
  "fieldMappings/createFieldMapping",
  async (data: CreateFieldMappingRequest, { rejectWithValue }) => {
    try {
      const mapping = await createFieldMapping(data);
      return mapping;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create field mapping"
      );
    }
  }
);

export const updateFieldMappingAction = createAsyncThunk(
  "fieldMappings/updateFieldMapping",
  async (
    { id, data }: { id: string; data: Partial<CreateFieldMappingRequest> },
    { rejectWithValue }
  ) => {
    try {
      const mapping = await updateFieldMapping(id, data);
      return mapping;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update field mapping"
      );
    }
  }
);

export const deleteFieldMappingAction = createAsyncThunk(
  "fieldMappings/deleteFieldMapping",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteFieldMapping(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete field mapping"
      );
    }
  }
);

export const activateFieldMappingAction = createAsyncThunk(
  "fieldMappings/activateFieldMapping",
  async (id: string, { rejectWithValue }) => {
    try {
      const mapping = await activateFieldMapping(id);
      return mapping;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to activate field mapping"
      );
    }
  }
);

export const deactivateFieldMappingAction = createAsyncThunk(
  "fieldMappings/deactivateFieldMapping",
  async (id: string, { rejectWithValue }) => {
    try {
      const mapping = await deactivateFieldMapping(id);
      return mapping;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to deactivate field mapping"
      );
    }
  }
);

// Slice
const fieldMappingsSlice = createSlice({
  name: "fieldMappings",
  initialState,
  reducers: {
    clearFieldMappingsError: (state) => {
      state.error = null;
    },
    setSelectedMapping: (state, action: PayloadAction<FieldMapping | null>) => {
      state.selectedMapping = action.payload;
    },
    setUploadProgress: (
      state,
      action: PayloadAction<{ isUploading: boolean; progress: number }>
    ) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch partner field mappings
      .addCase(fetchPartnerFieldMappingsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPartnerFieldMappingsAction.fulfilled,
        (state, action: PayloadAction<FieldMapping[]>) => {
          state.loading = false;
          state.mappings = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchPartnerFieldMappingsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch active field mapping
      .addCase(fetchActiveFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchActiveFieldMappingAction.fulfilled,
        (state, action: PayloadAction<FieldMapping | null>) => {
          state.loading = false;
          state.activeMapping = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchActiveFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch single field mapping
      .addCase(fetchFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFieldMappingAction.fulfilled,
        (state, action: PayloadAction<FieldMapping>) => {
          state.loading = false;
          state.selectedMapping = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create field mapping
      .addCase(createFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createFieldMappingAction.fulfilled,
        (state, action: PayloadAction<FieldMapping>) => {
          state.loading = false;
          state.mappings.push(action.payload);
          state.error = null;
        }
      )
      .addCase(createFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update field mapping
      .addCase(updateFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateFieldMappingAction.fulfilled,
        (state, action: PayloadAction<FieldMapping>) => {
          state.loading = false;
          const index = state.mappings.findIndex(
            (m) => m.id === action.payload.id
          );
          if (index !== -1) {
            state.mappings[index] = action.payload;
          }
          if (state.selectedMapping?.id === action.payload.id) {
            state.selectedMapping = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(updateFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete field mapping
      .addCase(deleteFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteFieldMappingAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.mappings = state.mappings.filter((m) => m.id !== action.payload);
          if (state.selectedMapping?.id === action.payload) {
            state.selectedMapping = null;
          }
          state.error = null;
        }
      )
      .addCase(deleteFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Activate field mapping
      .addCase(activateFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        activateFieldMappingAction.fulfilled,
        (state, action: PayloadAction<FieldMapping>) => {
          state.loading = false;
          // Deactivate all other mappings of the same partner/file type
          state.mappings = state.mappings.map((m) => {
            if (
              m.partnerId === action.payload.partnerId &&
              m.fileType === action.payload.fileType &&
              m.id !== action.payload.id
            ) {
              return { ...m, active: false };
            }
            if (m.id === action.payload.id) {
              return action.payload;
            }
            return m;
          });
          state.activeMapping = action.payload;
          state.error = null;
        }
      )
      .addCase(activateFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Deactivate field mapping
      .addCase(deactivateFieldMappingAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deactivateFieldMappingAction.fulfilled,
        (state, action: PayloadAction<FieldMapping>) => {
          state.loading = false;
          const index = state.mappings.findIndex(
            (m) => m.id === action.payload.id
          );
          if (index !== -1) {
            state.mappings[index] = action.payload;
          }
          if (state.activeMapping?.id === action.payload.id) {
            state.activeMapping = null;
          }
          state.error = null;
        }
      )
      .addCase(deactivateFieldMappingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const { clearFieldMappingsError, setSelectedMapping, setUploadProgress } =
  fieldMappingsSlice.actions;

// Selectors
export const selectFieldMappings = (state: RootState) =>
  state.fieldMappings.mappings;
export const selectActiveFieldMapping = (state: RootState) =>
  state.fieldMappings.activeMapping;
export const selectSelectedFieldMapping = (state: RootState) =>
  state.fieldMappings.selectedMapping;
export const selectFieldMappingsLoading = (state: RootState) =>
  state.fieldMappings.loading;
export const selectFieldMappingsError = (state: RootState) =>
  state.fieldMappings.error;
export const selectUploadProgress = (state: RootState) =>
  state.fieldMappings.uploadProgress;

export default fieldMappingsSlice.reducer;
